import { Button, CodeEntry, Heading, Modal, Text, useToast } from '@afterpaytouch/core'
import { Trans, useTranslation } from 'next-i18next'
import React, { FunctionComponent, useContext } from 'react'
import { useCodeEntry } from '../../../../hooks'
import { useAmplitudeWithEnduringEventProperties } from '../../../../integrations/amplitude'
import { TrackingEvent } from '../../../../model/amplitude'
import { useConsumerSelector } from '../../../../state'
import { useChangeEmailCheckEmailMutation, useChangeEmailVerifyEmailMutation } from '../../../../state/account/changeEmail'
import { EditEmailAddressModalContext, EditEmailAddressSteps } from '../../EditEmailAddressModal'
import { removeBiometricSettings } from '@afterpay/utils/biometric'

const I18N_NAMESPACE = ['common', 'account']

interface ConfirmProps {
  closeModal: () => void
}

export const Confirm: FunctionComponent<ConfirmProps> = ({ closeModal }) => {
  const { t } = useTranslation(I18N_NAMESPACE)
  const { logEvent } = useAmplitudeWithEnduringEventProperties()
  const { codeEntryRef, codeEntryKey, resetCodeEntry } = useCodeEntry()
  const { token, newEmail, setStep } = useContext(EditEmailAddressModalContext)
  const setToast = useToast()
  const { email, phoneNumber } = useConsumerSelector()

  const [checkEmail, { isError: isCheckEmailError, isLoading: isCheckEmailLoading }] = useChangeEmailCheckEmailMutation()

  const onCodeEntryComplete = async (code: string): Promise<void> => {
    try {
      await checkEmail({
        body: {
          code,
          token,
        },
      }).unwrap()
      logEvent(TrackingEvent.EDIT_EMAIL_SUCCESS)
      closeModal()
      setToast({
        message: t('account:edit:email:confirm:toast:success:message'),
        kind: 'Success',
        manualDismiss: true,
        toastTime: 2000,
        testNameSpace,
      })
      removeBiometricSettings(email, phoneNumber)
    } catch (err) {
      logEvent(TrackingEvent.EDIT_EMAIL_ERROR)
      resetCodeEntry()
    }
  }

  const [verifyEmail] = useChangeEmailVerifyEmailMutation()

  const onResendCode = async (): Promise<void> => {
    try {
      await verifyEmail({
        body: {
          newEmail,
          token,
        },
      }).unwrap()
    } catch (err) {}
  }

  const onGoBack = (): void => setStep(EditEmailAddressSteps.Edit)

  const isCodeEntryDisabled = isCheckEmailLoading

  const testNameSpace = 'edit-email-address-modal-confirm'

  return (
    <>
      <Modal.Header divider>
        <div className='text-center'>
          <Heading testNameSpace={testNameSpace} size='L'>
            {t('account:edit:email:confirm:header:heading')}
          </Heading>
        </div>
      </Modal.Header>
      <Modal.Content>
        <div>
          <div className='mt-2'>
            <Text>{t('account:edit:email:confirm:content:message', { newEmail })}</Text>
          </div>
          <div className='mt-4'>
            <CodeEntry
              codeEntryLength={6}
              onComplete={onCodeEntryComplete}
              disabled={isCodeEntryDisabled}
              error={isCheckEmailError}
              errorText={t('account:edit:email:confirm:content:code-entry:error')}
              testNameSpace={testNameSpace}
              ref={codeEntryRef}
              key={codeEntryKey}
            />
          </div>
          <div className='mt-6'>
            <Text size='S'>
              <Trans
                i18nKey='account:edit:email:confirm:content:resend'
                components={{
                  resendButton: <Button.Link size='Regular' onClick={onResendCode} testNameSpace={`${testNameSpace}-resend-code`} />,
                  goBackButton: <Button.Link size='Regular' onClick={onGoBack} testNameSpace={`${testNameSpace}-go-back`} />,
                }}
              />
            </Text>
          </div>
        </div>
      </Modal.Content>
    </>
  )
}
